/**
 * Mapbox popup body for a mountpoint marker.
 *
 * Renders mountpoint metadata, the publisher session (if any), and the list
 * of connected rovers — each with their current fix-quality badge.
 */

import type {
  ActiveConnection,
  Mountpoint,
  MountpointPosition,
  RoverPosition,
} from "@/types/api";
import { fixQualityMeta } from "@/lib/fixQuality";
import { formatAgo, formatBytes, formatDateTime } from "@/lib/utils";

interface Props {
  mountpoint: Mountpoint;
  position: MountpointPosition;
  /** Publisher connected (stream flag or /connections/active). */
  online: boolean;
  publisher?: ActiveConnection;
  rovers: ActiveConnection[];
  roverPositions: Map<string, RoverPosition>;
}

function formatCoord(d: number, isLat: boolean): string {
  const hemi = isLat ? (d >= 0 ? "N" : "S") : (d >= 0 ? "E" : "W");
  return `${Math.abs(d).toFixed(6)}° ${hemi}`;
}

export function MapMountpointPopup({
  mountpoint,
  position,
  online,
  publisher,
  rovers,
  roverPositions,
}: Props) {
  const fixMeta =
    online && position.fix_quality !== null ? fixQualityMeta(position.fix_quality) : null;
  const seeded = position.source === "SEED";
  const lastSeenIso = new Date(position.updated_at * 1000).toISOString();

  return (
    <div className="map-popup" data-testid="map-mountpoint-popup" data-online={online ? "true" : "false"}>
      <div className="map-popup__head">
        <div className="map-popup__title" data-testid="map-popup-title">{mountpoint.name}</div>
        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
          <span
            className={`badge ${online ? "badge--ok" : "badge--off"}`}
            data-testid="map-popup-status"
            title={online ? "La base está publicando" : "La base no está publicando; se muestra su última posición conocida"}
          >
            {online ? "Online" : "Offline"}
          </span>
          {fixMeta && (
            <span
              className={`badge ${fixMeta.text}`}
              style={{ background: `${fixMeta.hex}20`, borderColor: fixMeta.hex }}
              title="Fix quality reportado por NMEA GGA del publisher"
            >
              {fixMeta.label}
            </span>
          )}
          <span className={`badge ${mountpoint.is_enabled ? "badge--ok" : "badge--off"}`}>
            {mountpoint.is_enabled ? "Habilitado" : "Deshabilitado"}
          </span>
        </div>
      </div>
      {mountpoint.description && (
        <div className="map-popup__sub">{mountpoint.description}</div>
      )}
      {!online && (
        <div className="map-popup__sub" data-testid="map-popup-last-seen">
          Última publicación: <span className="mono">{formatDateTime(lastSeenIso)}</span>{" "}
          ({formatAgo(position.updated_at)})
        </div>
      )}
      {seeded && (
        <div className="map-popup__sub" style={{ color: "#92400e" }} data-testid="map-popup-seeded">
          Posición aproximada (±100 m, cargada a mano). Se sustituirá por la real en cuanto la base
          vuelva a publicar.
        </div>
      )}
      <dl className="map-popup__grid">
        <dt>Formato</dt>
        <dd className="mono">{mountpoint.format}</dd>
        <dt>Lat</dt>
        <dd className="mono">{formatCoord(position.lat, true)}</dd>
        <dt>Lon</dt>
        <dd className="mono">{formatCoord(position.lon, false)}</dd>
        {!seeded && (
          <>
            <dt>Alt</dt>
            <dd className="mono">{position.alt.toFixed(2)} m</dd>
          </>
        )}
        <dt>Fuente</dt>
        <dd className="mono">
          {seeded ? "Sembrada (aprox.)" : `${position.source} · staid=${position.station_id}`}
        </dd>
        {position.antenna_height !== null && (
          <>
            <dt>Antena</dt>
            <dd className="mono">{position.antenna_height!.toFixed(3)} m</dd>
          </>
        )}
      </dl>

      {fixMeta && (
        <div className="map-popup__section">
          <div className="map-popup__section-title">Solución (GGA upstream)</div>
          <dl className="map-popup__grid">
            <dt>Estado</dt>
            <dd className="mono">{fixMeta.label}</dd>
            {position.fix_num_sv !== null && (
              <>
                <dt>Satélites</dt>
                <dd className="mono">{position.fix_num_sv}</dd>
              </>
            )}
            {position.fix_hdop !== null && (
              <>
                <dt>HDOP</dt>
                <dd className="mono">{position.fix_hdop!.toFixed(2)}</dd>
              </>
            )}
          </dl>
        </div>
      )}

      {(Object.keys(position.sats_by_constellation).length > 0 ||
        position.recent_msg_types.length > 0) && (
        <div className="map-popup__section">
          <div className="map-popup__section-title">Observaciones</div>
          {Object.keys(position.sats_by_constellation).length > 0 && (
            <div className="map-popup__sats">
              {Object.entries(position.sats_by_constellation)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([c, n]) => (
                  <span key={c} className="map-popup__sat-pill">
                    <span className="map-popup__sat-c">{c}</span>
                    <span className="mono">{n}</span>
                  </span>
                ))}
              <span className="map-popup__sat-total">
                Total{" "}
                <strong>
                  {Object.values(position.sats_by_constellation).reduce(
                    (a, b) => a + b,
                    0,
                  )}
                </strong>{" "}
                sats
              </span>
            </div>
          )}
          {position.recent_msg_types.length > 0 && (
            <div className="map-popup__msg-types">
              <span className="muted">Mensajes:</span>{" "}
              <span className="mono">{position.recent_msg_types.join(", ")}</span>
            </div>
          )}
        </div>
      )}

      <div className="map-popup__section">
        <div className="map-popup__section-title">Publisher</div>
        {publisher ? (
          <dl className="map-popup__grid">
            <dt>Source user</dt>
            <dd className="mono">{publisher.client_username}</dd>
            <dt>Desde</dt>
            <dd className="mono">{formatDateTime(publisher.connected_since)}</dd>
            <dt>Bytes ↑</dt>
            <dd className="mono">{formatBytes(publisher.bytes_received)}</dd>
            <dt>IP</dt>
            <dd className="mono">{publisher.remote_addr || "—"}</dd>
          </dl>
        ) : (
          <div className="muted">Sin publisher conectado</div>
        )}
      </div>

      <div className="map-popup__section">
        <div className="map-popup__section-title">
          Rovers conectados ({rovers.length})
        </div>
        {rovers.length === 0 ? (
          <div className="muted">Sin rovers</div>
        ) : (
          <ul className="map-popup__rover-list">
            {rovers.map((rover) => {
              const rp = roverPositions.get(rover.session_id);
              const meta = rp ? fixQualityMeta(rp.quality) : null;
              return (
                <li key={rover.session_id}>
                  <span className="mono">{rover.client_username}</span>
                  {meta ? (
                    <span
                      className={`badge ${meta.text}`}
                      style={{ background: `${meta.hex}20`, borderColor: meta.hex }}
                    >
                      {meta.label}
                      {rp ? ` · ${rp.num_sv} sats` : ""}
                    </span>
                  ) : (
                    <span className="badge badge--off">Sin GGA</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
